import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError, timer } from 'rxjs';
import { AuthBootstrapService } from '../Services/Authorization/auth-bootstrap.service';
import { AuthService } from '../Services/Authorization/auth-service';
import { SKIP_AUTH } from '../Services/Authorization/skip-auth.token';

/**
 * Cold-start resilience for token-protected APIs.
 *
 * Two recovery paths, each fires at most once per request:
 *   - 401: the JWT in localStorage is missing/stale/not-yet-synced upstream.
 *     Refresh the JWT (which itself has 3 internal retries with backoff) and
 *     replay the request with the new Bearer header.
 *   - 0 / 5xx: transient network or server cold start. Wait 500ms and retry
 *     the request as-is.
 *
 * Skip patterns:
 *   - SKIP_AUTH context (per-request opt-out used elsewhere)
 *   - The auth-token endpoint itself, to avoid a refresh loop on its own 401.
 *
 * Position in interceptor chain: must run AFTER authInterceptor so the
 * retried request gets a fresh Bearer header. Registered last in app.config.ts.
 */
export const authRetryInterceptor: HttpInterceptorFn = (req, next) => {
  if (
    req.context.get(SKIP_AUTH) ||
    req.url.includes('GetAuthtokenForAppoinmentPlugin')
  ) {
    return next(req);
  }

  const auth = inject(AuthService);
  const bootstrap = inject(AuthBootstrapService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // 401 → refresh JWT, replay with new Bearer
      if (err?.status === 401) {
        return from(bootstrap.refreshToken()).pipe(
          switchMap(() => {
            const newToken = auth.token;
            if (!newToken) {
              // Still no token after refresh — propagate original error so
              // guards redirect to /noAccess.
              return throwError(() => err);
            }
            const retried = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` }
            });
            return next(retried);
          })
        );
      }

      // 0 (network) / 5xx (server cold-start) → single delayed retry
      if (err?.status === 0 || (err?.status >= 500 && err?.status < 600)) {
        return timer(500).pipe(switchMap(() => next(req)));
      }

      return throwError(() => err);
    })
  );
};
